// =============================================================================
// PER-ROLE PROPERTY FIELD VISIBILITY (PROD-9075)
// =============================================================================
//
// `properties: ['view']` is all-or-nothing: a role that can open a property
// sees the whole record, including WiFi passwords, access/lock codes, nightly
// rates, and workspace custom fields. Operators who want to share properties with an
// Ops/Maintenance role need the operational data without the sensitive data.
//
// This module adds named sensitive field groups on top of the existing role
// permission JSON, in the same shape as `reservationSensitiveData` and
// `mobileTeamView`. Both the server resolver (`@/lib/auth/server-permissions`)
// and the client hook (`@/hooks/use-permissions`) feed the same resolved
// permission record in here, so the property detail route and the property
// detail page agree on what a role may see.
//
// Groups default to VISIBLE. An existing role only loses a group once an
// administrator explicitly switches it off in Settings > Team & Permissions.

export const PROPERTY_SENSITIVE_FIELD_GROUPS_PERMISSION_KEY = 'propertySensitiveFieldGroups'

export const PROPERTY_SENSITIVE_FIELD_GROUPS = ['wifiAccess', 'financials', 'customFields'] as const

export type PropertySensitiveFieldGroup = (typeof PROPERTY_SENSITIVE_FIELD_GROUPS)[number]

export type PropertySensitiveFieldGroupAccess = Record<PropertySensitiveFieldGroup, boolean> & {
  /** `null` keeps the backwards-compatible "all custom fields" behavior. */
  customFieldKeys?: string[] | null
}

/**
 * Property base columns owned by each sensitive group. These are the
 * snake_case keys of the property detail API response, which are also the
 * `base_column_name` values the property detail schema uses for its fields, so
 * one list drives both server redaction and client field filtering.
 */
const PROPERTY_SENSITIVE_FIELD_KEYS: Record<PropertySensitiveFieldGroup, readonly string[]> = {
  customFields: ['custom_fields', 'unit_custom_fields', 'customFields'],
  financials: [
    'currency',
    'base_price',
    'base_price_usd',
    'weekend_base_price',
    'weekly_price_factor',
    'monthly_price_factor',
    'cleaning_fee',
    'security_deposit_fee',
    'extra_person_fee',
    'guests_included_in_regular_fee',
    'management_fee',
    'deal_value',
  ],
  wifiAccess: [
    'access_code',
    'wifi_name',
    'wifi_password',
    'intercom_name',
    'checkin_video_url',
    'has_smart_lock',
    'lock_provider',
    'lock_device_id',
    'shelly_device_id',
  ],
}

const OMITTED_PROPERTY_FIELD_KEYS = new Set([
  'custom_fields',
  'unit_custom_fields',
  'customFields',
  'unitCustomFields',
])

const ALL_GROUPS_VISIBLE: PropertySensitiveFieldGroupAccess = {
  customFieldKeys: null,
  customFields: true,
  financials: true,
  wifiAccess: true,
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readCustomFieldKeys(value: unknown): string[] | null {
  if (!Array.isArray(value)) {
    return null
  }

  const keys: string[] = []
  for (const item of value) {
    if (typeof item !== 'string' || item.length === 0 || keys.includes(item)) {
      continue
    }
    keys.push(item)
  }
  return keys
}

/**
 * Resolve which sensitive property field groups a role may see from its
 * resolved permission record. Missing or malformed settings mean "visible", so
 * roles that predate this feature keep the access they already had.
 */
export function resolvePropertySensitiveFieldGroups(
  permissions: unknown
): PropertySensitiveFieldGroupAccess {
  const config = isRecord(permissions)
    ? permissions[PROPERTY_SENSITIVE_FIELD_GROUPS_PERMISSION_KEY]
    : null
  if (!isRecord(config)) {
    return ALL_GROUPS_VISIBLE
  }

  const access: PropertySensitiveFieldGroupAccess = { ...ALL_GROUPS_VISIBLE }
  for (const group of PROPERTY_SENSITIVE_FIELD_GROUPS) {
    const value = config[group]
    if (typeof value === 'boolean') {
      access[group] = value
    }
  }
  access.customFieldKeys = readCustomFieldKeys(config.customFieldKeys)
  return access
}

export function canViewPropertyCustomField(permissions: unknown, fieldKey: string): boolean {
  const access = resolvePropertySensitiveFieldGroups(permissions)
  if (!access.customFields) {
    return false
  }
  return !Array.isArray(access.customFieldKeys) || access.customFieldKeys.includes(fieldKey)
}

export function canViewPropertySensitiveFieldGroup(
  permissions: unknown,
  group: PropertySensitiveFieldGroup
): boolean {
  return resolvePropertySensitiveFieldGroups(permissions)[group]
}

/**
 * The stored group map when a role has one, else `null`. Used by the settings
 * save path, which allowlists the keys it persists — a group map that is absent
 * or malformed means "defaults", which is not worth storing.
 */
export function readStoredPropertySensitiveFieldGroups(
  permissions: unknown
): PropertySensitiveFieldGroupAccess | null {
  const config = isRecord(permissions)
    ? permissions[PROPERTY_SENSITIVE_FIELD_GROUPS_PERMISSION_KEY]
    : null
  if (!isRecord(config)) {
    return null
  }

  return resolvePropertySensitiveFieldGroups(permissions)
}

/**
 * Apply a single group switch to a role's permission record and return the
 * value to store under {@link PROPERTY_SENSITIVE_FIELD_GROUPS_PERMISSION_KEY}.
 * Returns `null` once every group is visible again, which means the caller
 * should drop the key so stored roles stay close to their original shape.
 */
export function nextPropertySensitiveFieldGroups(
  permissions: unknown,
  group: PropertySensitiveFieldGroup,
  visible: boolean
): PropertySensitiveFieldGroupAccess | null {
  const access: PropertySensitiveFieldGroupAccess = {
    ...resolvePropertySensitiveFieldGroups(permissions),
    [group]: visible,
  }

  if (
    PROPERTY_SENSITIVE_FIELD_GROUPS.every((key) => access[key]) &&
    access.customFieldKeys === null
  ) {
    return null
  }
  return access
}

export function nextPropertyCustomFieldKeys(
  permissions: unknown,
  customFieldKeys: readonly string[] | null
): PropertySensitiveFieldGroupAccess | null {
  const access: PropertySensitiveFieldGroupAccess = {
    ...resolvePropertySensitiveFieldGroups(permissions),
    customFieldKeys: customFieldKeys === null ? null : Array.from(new Set(customFieldKeys)),
  }

  if (
    PROPERTY_SENSITIVE_FIELD_GROUPS.every((key) => access[key]) &&
    access.customFieldKeys === null
  ) {
    return null
  }
  return access
}

/**
 * Property response keys the role may not see. Empty when every group is
 * visible, which is the default for every role.
 */
export function hiddenPropertyFieldKeys(
  access: PropertySensitiveFieldGroupAccess
): readonly string[] {
  const hidden: string[] = []
  for (const group of PROPERTY_SENSITIVE_FIELD_GROUPS) {
    if (access[group]) {
      continue
    }
    hidden.push(...PROPERTY_SENSITIVE_FIELD_KEYS[group])
  }
  return hidden
}

export function isPropertyFieldHidden(
  access: PropertySensitiveFieldGroupAccess,
  fieldKey: string
): boolean {
  return hiddenPropertyFieldKeys(access).includes(fieldKey)
}

/**
 * Null out the sensitive property fields the role may not see on one API
 * response row. Returns the row untouched when every group is visible, which
 * keeps the default path allocation-free.
 */
export function redactHiddenPropertyFields<T>(
  row: T,
  access: PropertySensitiveFieldGroupAccess
): T {
  const hiddenKeys = hiddenPropertyFieldKeys(access)
  const hasGranularCustomFieldAccess = access.customFields && Array.isArray(access.customFieldKeys)
  if (hiddenKeys.length === 0 && !hasGranularCustomFieldAccess) {
    return row
  }
  if (typeof row !== 'object' || row === null || Array.isArray(row)) {
    return row
  }

  const visibleRow = { ...row }

  const hiddenAliases = hiddenKeys.flatMap((fieldKey) => [
    fieldKey,
    fieldKey.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase()),
  ])
  for (const fieldKey of new Set(hiddenAliases)) {
    if (!(fieldKey in visibleRow)) {
      continue
    }
    if (OMITTED_PROPERTY_FIELD_KEYS.has(fieldKey)) {
      Reflect.deleteProperty(visibleRow, fieldKey)
    } else {
      Reflect.set(visibleRow, fieldKey, null)
    }
  }

  if (hasGranularCustomFieldAccess) {
    for (const fieldKey of OMITTED_PROPERTY_FIELD_KEYS) {
      if (!(fieldKey in visibleRow)) {
        continue
      }
      Reflect.set(
        visibleRow,
        fieldKey,
        filterCustomFieldValues(Reflect.get(visibleRow, fieldKey), access.customFieldKeys ?? [])
      )
    }
  }
  return visibleRow
}

function filterCustomFieldValues(value: unknown, allowedKeys: readonly string[]): unknown {
  if (!isRecord(value)) {
    return {}
  }

  const allowed = new Set(allowedKeys)
  const pick = (record: Record<string, unknown>): Record<string, unknown> => {
    const result: Record<string, unknown> = {}
    for (const [key, fieldValue] of Object.entries(record)) {
      if (allowed.has(key)) {
        result[key] = fieldValue
      }
    }
    return result
  }

  return isRecord(value.fields) ? { fields: pick(value.fields) } : pick(value)
}
